import React, { useEffect, useState, memo } from 'react';

/**
 * AmbassadorService - Referral Tracking & Leaderboard
 * 
 * Captures incoming UTM-tagged visits, attributes them to the local user via
 * a stable referrer ID, and surfaces a "Social Ambassador" leaderboard.
 * 
 * Architecture:
 * - When a user shares, their referral_id is embedded in the UTM (utm_content)
 * - When a new visitor arrives with utm_source=share, we credit the referrer
 * - Top ambassadors earn bonus coins redeemable for exclusive cosmetics
 */

export interface AmbassadorStats {
  referralId: string;
  totalReferrals: number;
  totalEarnedCoins: number;
  lastReferralAt: string | null;
  redeemedCosmetics: string[];
}

const STORAGE_KEY = 'ambassador_stats';
const REFERRAL_ID_KEY = 'referral_id';

// =====  LAUNCH CONTROLS  =====
// LAUNCH_MODE=true activates the Global Championship multiplier.
// Flip back to `false` after the championship window closes to restore the
// baseline reward rate. AMBASSADOR_MULTIPLIER is a single source of truth for
// the per-referral coin grant.
export const LAUNCH_MODE = true;
export const AMBASSADOR_MULTIPLIER = LAUNCH_MODE ? 3 : 1;
const BASE_COINS_PER_REFERRAL = 25;
const COINS_PER_REFERRAL = BASE_COINS_PER_REFERRAL * AMBASSADOR_MULTIPLIER;

// Generate or retrieve stable referral ID for current user
export function getReferralId(): string {
  if (typeof window === 'undefined') return 'anonymous';
  let id = localStorage.getItem(REFERRAL_ID_KEY);
  if (!id) {
    id = `ref_${Math.random().toString(36).slice(2, 10)}_${Date.now().toString(36)}`;
    localStorage.setItem(REFERRAL_ID_KEY, id);
  }
  return id;
}

// Load current user's ambassador stats
export function getAmbassadorStats(): AmbassadorStats {
  if (typeof window === 'undefined') {
    return {
      referralId: 'anonymous',
      totalReferrals: 0,
      totalEarnedCoins: 0,
      lastReferralAt: null,
      redeemedCosmetics: [],
    };
  }
  const raw = localStorage.getItem(STORAGE_KEY);
  if (raw) {
    try {
      return JSON.parse(raw);
    } catch {
      // fall through
    }
  }
  return {
    referralId: getReferralId(),
    totalReferrals: 0,
    totalEarnedCoins: 0,
    lastReferralAt: null,
    redeemedCosmetics: [],
  };
}

// Credit a successful referral (called when a UTM-tagged visitor arrives)
export function creditReferral(referrerId: string): void {
  if (typeof window === 'undefined') return;
  const stats = getAmbassadorStats();
  
  // Only credit if this visit was actually from someone else
  if (referrerId === stats.referralId) return;
  
  const updated: AmbassadorStats = {
    ...stats,
    totalReferrals: stats.totalReferrals + 1,
    totalEarnedCoins: stats.totalEarnedCoins + COINS_PER_REFERRAL,
    lastReferralAt: new Date().toISOString(),
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  
  // Push to GA4
  if ((window as any).gtag) {
    (window as any).gtag('event', 'referral_credited', {
      'referrer_id': referrerId,
      'coins_earned': COINS_PER_REFERRAL,
      'total_referrals': updated.totalReferrals,
    });
  }
}

// Capture incoming UTM referrer on page load
export function captureIncomingReferral(): void {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  const utmSource = params.get('utm_source');
  const utmContent = params.get('utm_content'); // referrer's ID
  
  if (utmSource === 'share' && utmContent) {
    const visitKey = `referral_visit_${utmContent}_${new Date().toISOString().slice(0, 10)}`;
    if (!localStorage.getItem(visitKey)) {
      creditReferral(utmContent);
      localStorage.setItem(visitKey, '1');
      
      // Check whether the referrer (current user) just crossed a tier threshold
      // Note: This actually credits the OTHER user; we only auto-grant on visits to OUR own referral panel.
    }
  }
  
  // Also check whether the current viewer (the referrer themselves) has crossed any tier
  const stats = getAmbassadorStats();
  checkAndGrantTier(stats.totalReferrals);
}

// ===== AmbassadorTierGate: Auto-grant cosmetics on tier milestone =====

export interface TierGrant {
  threshold: number;
  cosmeticId: string;
  cosmeticName: string;
}

export const TIER_GRANTS: TierGrant[] = [
  { threshold: 5, cosmeticId: 'cosmetic_chrome', cosmeticName: 'Chrome Polish' },
  { threshold: 25, cosmeticId: 'cosmetic_neon', cosmeticName: 'Neon Glow' },
  { threshold: 100, cosmeticId: 'cosmetic_gold', cosmeticName: 'Gold Champion' },
];

const TIER_GRANT_SEEN_KEY = 'ambassador_tier_grants_seen';

/**
 * Check whether a tier auto-grant should fire for the current referral count.
 * Returns the newly granted tier (if any), null otherwise.
 * Idempotent: tracks which tiers have already been granted in localStorage.
 */
export function checkAndGrantTier(totalReferrals: number): TierGrant | null {
  if (typeof window === 'undefined') return null;
  let seen: number[] = [];
  try {
    seen = JSON.parse(localStorage.getItem(TIER_GRANT_SEEN_KEY) || '[]');
  } catch {
    // ignore
  }
  
  const stats = getAmbassadorStats();
  
  // Find the highest unseen tier the user has crossed
  for (let i = TIER_GRANTS.length - 1; i >= 0; i--) {
    const tier = TIER_GRANTS[i];
    if (totalReferrals >= tier.threshold && !seen.includes(tier.threshold)) {
      // Auto-grant the cosmetic (free, doesn't cost coins)
      if (!stats.redeemedCosmetics.includes(tier.cosmeticId)) {
        const updated: AmbassadorStats = {
          ...stats,
          redeemedCosmetics: [...stats.redeemedCosmetics, tier.cosmeticId],
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      }
      seen.push(tier.threshold);
      localStorage.setItem(TIER_GRANT_SEEN_KEY, JSON.stringify(seen));
      
      // GA4 tracking
      if ((window as any).gtag) {
        (window as any).gtag('event', 'cosmetic_auto_granted', {
          'cosmetic_id': tier.cosmeticId,
          'cosmetic_name': tier.cosmeticName,
          'tier_threshold': tier.threshold,
          'total_referrals': totalReferrals,
          'timestamp': new Date().toISOString(),
        });
      }
      
      // Emit DOM event for unlock animation listeners
      window.dispatchEvent(
        new CustomEvent('ambassador-tier-granted', {
          detail: tier,
        })
      );
      
      return tier;
    }
  }
  return null;
}

// Cosmetic Store: items redeemable with ambassador coins
export interface CosmeticItem {
  id: string;
  name: string;
  description: string;
  cost: number;
  icon: string;
}

export const COSMETIC_STORE: CosmeticItem[] = [
  { id: 'cosmetic_chrome', name: 'Chrome Polish', description: 'Glossy metallic T-Rex skin', cost: 100, icon: '✨' },
  { id: 'cosmetic_retro', name: 'Retro Pixel', description: '8-bit color palette throwback', cost: 150, icon: '🕹️' },
  { id: 'cosmetic_neon', name: 'Neon Glow', description: 'Electric pink and cyan trim', cost: 200, icon: '💫' },
  { id: 'cosmetic_gold', name: 'Gold Champion', description: 'Reserved for top ambassadors', cost: 500, icon: '🏆' },
];

// Redeem a cosmetic item
export function redeemCosmetic(itemId: string): { success: boolean; message: string } {
  const item = COSMETIC_STORE.find((c) => c.id === itemId);
  if (!item) return { success: false, message: 'Item not found' };
  
  const stats = getAmbassadorStats();
  if (stats.redeemedCosmetics.includes(itemId)) {
    return { success: false, message: 'Already redeemed' };
  }
  if (stats.totalEarnedCoins < item.cost) {
    return { success: false, message: `Need ${item.cost - stats.totalEarnedCoins} more coins` };
  }
  
  const updated: AmbassadorStats = {
    ...stats,
    totalEarnedCoins: stats.totalEarnedCoins - item.cost,
    redeemedCosmetics: [...stats.redeemedCosmetics, itemId],
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  
  // Push to GA4
  if (typeof window !== 'undefined' && (window as any).gtag) {
    (window as any).gtag('event', 'cosmetic_redeemed', {
      'cosmetic_id': itemId,
      'cost': item.cost,
    });
  }
  
  return { success: true, message: `${item.name} unlocked!` };
}

/**
 * AmbassadorPanel - Renders the Ambassador stats card and cosmetic store.
 * Drop into Leaderboard.tsx or any page that needs to surface the program.
 */
export const AmbassadorPanel: React.FC = memo(() => {
  const [stats, setStats] = useState<AmbassadorStats>(getAmbassadorStats);
  const [feedback, setFeedback] = useState<string | null>(null);

  // Capture incoming UTM referrals on mount
  useEffect(() => {
    captureIncomingReferral();
    setStats(getAmbassadorStats());
  }, []);

  const handleRedeem = (itemId: string) => {
    const result = redeemCosmetic(itemId);
    setFeedback(result.message);
    setStats(getAmbassadorStats());
    setTimeout(() => setFeedback(null), 3000);
  };

  return (
    <div
      style={{
        background: 'var(--card-bg)',
        border: '1px solid var(--border-color)',
        borderRadius: '8px',
        padding: '24px',
        marginBottom: '32px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
        <span style={{ fontSize: '28px' }}>🌟</span>
        <div>
          <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: 'var(--text-color)' }}>
            Social Ambassador
          </h3>
          <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary)' }}>
            Earn coins for every friend who joins via your share link
          </p>
        </div>
      </div>

      {/* Next Tier Progress */}
      <NextTierProgress totalReferrals={stats.totalReferrals} />

      {/* Stats Row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '24px' }}>
        <div style={{ padding: '12px', background: 'var(--bg-color)', borderRadius: '4px', textAlign: 'center' }}>
          <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px' }}>REFERRALS</div>
          <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--accent, #FF6B35)' }}>
            {stats.totalReferrals}
          </div>
        </div>
        <div style={{ padding: '12px', background: 'var(--bg-color)', borderRadius: '4px', textAlign: 'center' }}>
          <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px' }}>COINS</div>
          <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--accent, #FF6B35)' }}>
            {stats.totalEarnedCoins}
          </div>
        </div>
        <div style={{ padding: '12px', background: 'var(--bg-color)', borderRadius: '4px', textAlign: 'center' }}>
          <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px' }}>UNLOCKED</div>
          <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--accent, #FF6B35)' }}>
            {stats.redeemedCosmetics.length}
          </div>
        </div>
      </div>

      {/* Cosmetic Store */}
      <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: 'var(--text-color)' }}>
        Cosmetic Store
      </h4>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px' }}>
        {COSMETIC_STORE.map((item) => {
          const owned = stats.redeemedCosmetics.includes(item.id);
          const canAfford = stats.totalEarnedCoins >= item.cost;
          return (
            <div
              key={item.id}
              style={{
                padding: '12px',
                background: 'var(--bg-color)',
                border: owned ? '2px solid #4CAF50' : '1px solid var(--border-color)',
                borderRadius: '6px',
                opacity: owned ? 0.7 : 1,
              }}
            >
              <div style={{ fontSize: '24px', marginBottom: '4px' }}>{item.icon}</div>
              <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-color)', marginBottom: '2px' }}>
                {item.name}
              </div>
              <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '8px', minHeight: '28px' }}>
                {item.description}
              </div>
              <button
                onClick={() => handleRedeem(item.id)}
                disabled={owned || !canAfford}
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  background: owned ? '#4CAF50' : canAfford ? 'var(--accent, #FF6B35)' : 'var(--border-color)',
                  color: owned || canAfford ? '#fff' : 'var(--text-secondary)',
                  border: 'none',
                  borderRadius: '3px',
                  fontSize: '11px',
                  fontWeight: '600',
                  cursor: owned || !canAfford ? 'not-allowed' : 'pointer',
                  transition: 'background 0.2s ease',
                }}
              >
                {owned ? '✓ Owned' : `${item.cost} coins`}
              </button>
            </div>
          );
        })}
      </div>

      {feedback && (
        <div
          style={{
            marginTop: '16px',
            padding: '8px 12px',
            background: '#E8F5E9',
            border: '1px solid #4CAF50',
            color: '#2E7D32',
            borderRadius: '4px',
            fontSize: '12px',
            textAlign: 'center',
          }}
        >
          {feedback}
        </div>
      )}
    </div>
  );
});

AmbassadorPanel.displayName = 'AmbassadorPanel';

// Tier progression metadata
interface TierDef {
  threshold: number;
  name: string;
  icon: string;
  rewardId: string;
  rewardLabel: string;
}

const TIER_PROGRESSION: TierDef[] = [
  { threshold: 5, name: 'Bronze', icon: '🥉', rewardId: 'cosmetic_chrome', rewardLabel: 'Chrome Polish skin' },
  { threshold: 25, name: 'Silver', icon: '🥈', rewardId: 'cosmetic_neon', rewardLabel: 'Neon Glow skin' },
  { threshold: 100, name: 'Gold', icon: '🥇', rewardId: 'cosmetic_gold', rewardLabel: 'Gold Champion skin' },
];

const NextTierProgress = memo(({ totalReferrals }: { totalReferrals: number }) => {
  // Find next tier the user has not yet hit
  const nextTier = TIER_PROGRESSION.find((t) => totalReferrals < t.threshold);
  
  // All tiers complete
  if (!nextTier) {
    return (
      <div
        style={{
          padding: '12px 16px',
          background: 'linear-gradient(90deg, #FFD700, #FFA500)',
          borderRadius: '6px',
          marginBottom: '20px',
          color: '#202124',
          textAlign: 'center',
          fontWeight: '600',
          fontSize: '13px',
        }}
      >
        🏆 You’ve reached Gold tier — all cosmetics unlocked!
      </div>
    );
  }
  
  const prevThreshold = TIER_PROGRESSION[TIER_PROGRESSION.indexOf(nextTier) - 1]?.threshold ?? 0;
  const progress = Math.min(
    Math.max((totalReferrals - prevThreshold) / (nextTier.threshold - prevThreshold), 0),
    1
  );
  const remaining = nextTier.threshold - totalReferrals;
  
  return (
    <div
      style={{
        padding: '14px 16px',
        background: 'var(--bg-color)',
        border: '1px solid var(--border-color)',
        borderRadius: '6px',
        marginBottom: '20px',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '10px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '20px' }}>{nextTier.icon}</span>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-color)' }}>
              Next: {nextTier.name} Ambassador
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
              {remaining} more referral{remaining === 1 ? '' : 's'} to unlock
            </div>
          </div>
        </div>
        <span
          style={{
            fontSize: '10px',
            fontWeight: '700',
            color: '#FF6B35',
            background: '#FFF4E6',
            padding: '4px 8px',
            borderRadius: '12px',
            border: '1px solid #FFD4A8',
            whiteSpace: 'nowrap',
          }}
        >
          COMING SOON
        </span>
      </div>
      
      <div
        style={{
          width: '100%',
          height: '8px',
          background: 'var(--border-color)',
          borderRadius: '4px',
          overflow: 'hidden',
          marginBottom: '8px',
        }}
      >
        <div
          style={{
            width: `${progress * 100}%`,
            height: '100%',
            background: 'linear-gradient(90deg, #FF6B35, #FFA500)',
            transition: 'width 0.4s cubic-bezier(0.23, 1, 0.32, 1)',
          }}
        />
      </div>
      
      <div
        style={{
          fontSize: '11px',
          color: 'var(--text-secondary)',
          textAlign: 'center',
          fontStyle: 'italic',
        }}
      >
        Unlock reward: <strong style={{ color: 'var(--text-color)' }}>{nextTier.rewardLabel}</strong>
      </div>
    </div>
  );
});
NextTierProgress.displayName = 'NextTierProgress';
